// stores/authStore.js
import { defineStore } from 'pinia';
import backendCall from "../services/AxiosService";
import axios from "axios";
import * as Sentry from "@sentry/browser";
import router from "@/router";
import { reportError } from "../utilities/errorReporting";

interface User {
    id: number;
    username: string;
    email?: string;
    roles: number[];
    permissions?: string[];
    firstName: string;
    lastName: string;
    has_passkeys?: boolean;
    isActive?: boolean;
    createdAt?: string;
    updatedAt?: string;
    phoneNumber?: string;
    department?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const useAuthStore = defineStore('auth', {
    state: () => {
        // Initialize dark mode from localStorage
        const storedTheme = localStorage.getItem('theme');
        const isDarkMode = storedTheme === 'dark';

        // Apply theme on initial load
        const html = document.querySelector('html');
        if (html) {
            if (isDarkMode) {
                html.classList.add('dark');
            } else {
                html.classList.remove('dark');
            }
            // Set default theme in localStorage if not set
            if (!storedTheme) {
                localStorage.setItem('theme', 'light');
            }
        }

        return {
            isAuthenticated: false,
            user: null as User | null,
            authCheckPromise: null as Promise<void> | null,
            lastAuthCheck: 0, // Timestamp of the last auth check
            authCheckInterval: 10 * 60 * 1000, // 10 minutes in milliseconds
            darkMode: isDarkMode,
        };
    },
    getters: {
        hasPermission: (state) => (permission: string) => {
            if (!state.user) {
                return false;
            }

            // Safety check: ensure permission is defined
            if (!permission) {
                console.warn('hasPermission called with undefined permission');
                return false;
            }

            // Check if user has superadmin permission
            if (state.user.permissions?.includes('*')) {
                return true;
            }

            // Check if user has the specific permission
            if (state.user.permissions?.includes(permission)) {
                return true;
            }

            // Check for wildcard resource permission (e.g., 'users:*')
            if (permission.includes(':')) {
                const [resource] = permission.split(':');
                if (state.user.permissions?.includes(`${resource}:*`)) {
                    return true;
                }
            }

            return false;
        },
        hasAnyPermission: (state) => (permissions: string[]) => {
            if (!state.user) return false;
            return permissions.some(permission => {
                const authStore = useAuthStore();
                return authStore.hasPermission(permission);
            });
        },
        hasAllPermissions: (state) => (permissions: string[]) => {
            if (!state.user) return false;
            return permissions.every(permission => {
                const authStore = useAuthStore();
                return authStore.hasPermission(permission);
            });
        },
        isAdmin: (state) => {
            return state.user?.permissions?.includes('*') ||
                   state.user?.permissions?.includes('admin:access') ||
                   state.user?.permissions?.includes('admin:read') ||
                   state.user?.permissions?.includes('admin:manage') ||
                   false;
        },
        permissions: (state) => {
            return state.user?.permissions || [];
        }
    },
    actions: {
        async checkAuthStatus(force = false) {
            // If there's already a check in progress, return that promise
            if (this.authCheckPromise) {
                return this.authCheckPromise;
            }

            // Skip check if we've checked recently, unless forced
            const now = Date.now();
            if (!force && this.lastAuthCheck > 0 && (now - this.lastAuthCheck < this.authCheckInterval)) {
                return Promise.resolve();
            }

            this.authCheckPromise = backendCall.get('/auth/check')
                .then(response => {
                    this.isAuthenticated = !!response.data.user;
                    this.user = response.data.user;
                    this.lastAuthCheck = Date.now();
                    
                    // Ensure permissions are loaded
                    if (this.user && !this.user.permissions) {
                        console.warn('User loaded without permissions, refreshing session...');
                        return this.refreshSession();
                    }
                })
                .catch((error) => {
                    // Only clear auth if it's actually a 401 error
                    // This prevents network errors from logging users out
                    if (error?.response?.status === 401) {
                        this.isAuthenticated = false;
                        this.user = null;
                    } else {
                        // For other errors, keep current auth state
                        console.error('Auth check failed but not clearing session:', error);
                        // Report unexpected auth check failures to Sentry
                        reportError(error, {
                            action: 'checkAuthStatus',
                            forced: force,
                            currentAuthState: this.isAuthenticated
                        });
                    }
                })
                .finally(() => {
                    this.authCheckPromise = null;
                });

            return this.authCheckPromise;
        },

        async login(credentials: { username: string, password: string }) {
            const response = await backendCall.post('/auth/login', credentials);
            this.isAuthenticated = true;
            this.user = response.data.user;

            // Try to refresh session for latest permissions, but don't fail login if this fails
            // This prevents race conditions where refreshSession failure causes broken auth state
            try {
                const refreshResult = await this.refreshSession();
                if (!refreshResult.success) {
                    console.warn('Session refresh after login failed, using initial user data:', refreshResult.message);
                    // Still update lastAuthCheck so we don't immediately trigger 401 handling
                    this.lastAuthCheck = Date.now();
                }
            } catch (refreshError) {
                console.warn('Session refresh after login threw error:', refreshError);
                // Update lastAuthCheck to prevent immediate 401 logout race condition
                this.lastAuthCheck = Date.now();
            }

            Sentry.setUser({
                id: this.user.id.toString(),
                username: this.user.username,
                email: this.user.email,
                fullName: `${this.user.firstName} ${this.user.lastName}`.trim(),
            });

            Sentry.setContext('user_details', {
                roles: this.user.roles,
                permissions: this.user.permissions,
                firstName: this.user.firstName,
                lastName: this.user.lastName,
                department: this.user.department,
                hasPasskeys: this.user.has_passkeys
            });
            return response;
        },

        async refreshSession() {
            try {
                const response = await backendCall.post('/auth/refresh');
                if (response.data.success && response.data.user) {
                    // Update the local user state with fresh data
                    this.user = response.data.user;
                    this.isAuthenticated = true;
                    this.lastAuthCheck = Date.now();
                    
                    // Update Sentry context
                    Sentry.setUser({
                        id: response.data.user.id.toString(),
                        username: response.data.user.username,
                        email: response.data.user.email,
                        fullName: `${response.data.user.firstName} ${response.data.user.lastName}`.trim(),
                    });
                    
                    Sentry.setContext('user_details', {
                        roles: response.data.user.roles,
                        permissions: response.data.user.permissions,
                        firstName: response.data.user.firstName,
                        lastName: response.data.user.lastName,
                        department: response.data.user.department,
                        hasPasskeys: response.data.user.has_passkeys
                    });
                    
                    console.log('Session refreshed successfully:', {
                        userId: response.data.user.id,
                        permissionCount: response.data.user.permissions?.length || 0,
                        hasAdminAccess: response.data.user.permissions?.includes('admin:access')
                    });
                    
                    return { success: true, user: response.data.user };
                }
                return { success: false, message: response.data.message || 'Failed to refresh session' };
            } catch (error: any) {
                console.error('Session refresh error:', error);
                
                // Provide more specific error messages
                let message = 'Error refreshing session';
                
                if (error.response?.status === 401) {
                    message = 'Your session has expired. Please log in again.';
                } else if (error.response?.status === 403) {
                    message = 'You do not have permission to refresh the session.';
                } else if (error.response?.status === 500) {
                    message = 'Server error occurred. Please try again later.';
                } else if (!error.response) {
                    message = 'Network error. Please check your connection.';
                }
                
                reportError(error, { 
                    action: 'refreshSession',
                    status: error.response?.status,
                    message: error.response?.data?.message
                });
                
                return { success: false, message };
            }
        },

        async logout() {
            try {
                await axios.get(`${API_BASE_URL}auth/logout`, {withCredentials: true});
            } catch (error) {
                console.error('Logout error:', error);
                // Continue with local logout even if server logout fails
            }

            this.isAuthenticated = false;
            this.user = null;
            this.lastAuthCheck = 0;
            Sentry.setUser(null);

            await router.push('/login');
        },

        toggleDarkMode() {
            this.darkMode = !this.darkMode;
            const element = document.querySelector('html');
            if (element) {
                if (this.darkMode) {
                    element.classList.add('dark');
                } else {
                    element.classList.remove('dark');
                }
            }
            localStorage.setItem('theme', this.darkMode ? 'dark' : 'light');
        },
        
        // Alias for checkAuthStatus for backward compatibility
        async checkAuth(force = false) {
            return this.checkAuthStatus(force);
        }
    }
});
